import React from 'react';
import { Button, Card, StatusChip, Text } from '@fragments';
import { IconAlertTriangle, IconArrowUpRight, IconCoins, IconDownload, IconExternalLink, IconReceipt, IconRefresh } from '@tabler/icons-react';
import { formatCredits, formatDate, formatDay, formatMoney } from '../../../../data/payments';
import './styles.scss';

const CHIPS = { paid: StatusChip.Success, open: StatusChip.Alert, void: StatusChip.Default, uncollectible: StatusChip.Error, draft: StatusChip.Default };
const REASONS = { subscription_create: 'billingInvoice_create', subscription_cycle: 'billingInvoice_cycle', subscription_update: 'billingInvoice_update' };
const SKELETONS = [0, 1, 2];

// `GET /studio/invoices`, newest first: subscription invoices (subscribed, renewed, upgraded)
// and credit packs, with amount, status and Stripe's own pages — the hosted invoice (receipt,
// or pay an open one) and the PDF. Ten at a time.
export default function InvoiceList({ studio: s, page }) {
  const { t } = s;
  const locale = s.locale.replace('_', '-');
  const { status, list, page: current, lastPage } = page.invoices;

  const describe = invoice => {
    if (invoice.kind === 'credits') return invoice.units ? t('creditsPackSummary', { credits: formatCredits(invoice.units, locale) }) : t('creditsPackEyebrow');
    const plan = t(`planName_${invoice.plan || 'free'}`);
    return REASONS[invoice.billing_reason] ? t(REASONS[invoice.billing_reason], { plan }) : plan;
  };
  const meta = invoice => [
    formatDate(invoice.timestamps.created, locale),
    invoice.period_start && invoice.period_end && `${formatDay(invoice.period_start, locale)} – ${formatDay(invoice.period_end, locale)}`,
    invoice.number && t('billingInvoiceNumber', { number: invoice.number }),
  ].filter(Boolean).join(' · ');

  const row = invoice => {
    const Chip = CHIPS[invoice.status] || StatusChip.Default;
    const open = invoice.status === 'open';
    return <li key={invoice.id} className="studio-invoice">
      <span className="studio-invoice__icon" aria-hidden="true">{invoice.kind === 'credits' ? <IconCoins /> : <IconReceipt />}</span>
      <div className="studio-invoice__text">
        <Text.label contrast={9}>{describe(invoice)}</Text.label>
        <Text.caption contrast={6}>{meta(invoice)}</Text.caption>
      </div>
      <div className="studio-invoice__amount">
        <Text.label contrast={9}>{formatMoney(open ? invoice.amount_due : invoice.total, invoice.currency, locale)}</Text.label>
        <Chip preserveCase text={t(`billingInvoiceStatus_${invoice.status}`, { defaultValue: invoice.status })} />
      </div>
      <div className="studio-invoice__actions">
        {open && invoice.hosted_invoice_url
          ? <Button size="small" variant="rounded" color="inverse" preventLoading endIcon={<IconArrowUpRight />} onClick={() => page.openUrl(invoice.hosted_invoice_url)}>{t('billingInvoicePay')}</Button>
          : invoice.hosted_invoice_url && <Button size="small" variant="text" color="overlay" preventLoading icon={<IconExternalLink />} aria-label={t('billingInvoiceView')} tooltip={t('billingInvoiceView')} onClick={() => page.openUrl(invoice.hosted_invoice_url)} />}
        {invoice.invoice_pdf && <Button size="small" variant="text" color="overlay" preventLoading icon={<IconDownload />} aria-label={t('billingInvoicePdf')} tooltip={t('billingInvoicePdf')} onClick={() => page.openUrl(invoice.invoice_pdf)} />}
      </div>
    </li>;
  };

  const body = status === 'loading' || status === 'idle' ? <ul className="studio-invoices" aria-busy="true">
      {SKELETONS.map(n => <li key={n} className="studio-invoice">
        <span className="studio-invoice__icon" aria-hidden="true"><IconReceipt /></span>
        <div className="studio-invoice__text"><Text.label loading>{t('billingInvoicesTitle')}</Text.label><Text.caption loading>{t('billingInvoicesTitle')}</Text.caption></div>
      </li>)}
    </ul>
    : status === 'error' ? <div className="studio-invoices__state" role="alert">
      <IconAlertTriangle aria-hidden="true" />
      <Text.p contrast={7}>{t('billingInvoicesError')}</Text.p>
      <Button size="small" variant="rounded" color="overlay" startIcon={<IconRefresh />} onClick={page.reloadInvoices}>{t('retry')}</Button>
    </div>
    : !list.length ? <div className="studio-invoices__state">
      <IconReceipt aria-hidden="true" />
      <Text.label contrast={9}>{t('billingInvoicesEmpty')}</Text.label>
      <Text.caption contrast={6}>{t('billingInvoicesEmptyHint')}</Text.caption>
    </div>
    : <ul className="studio-invoices">{list.map(row)}</ul>;

  const more = status !== 'error' && current < lastPage && <Button size="small" variant="text" color="overlay" onClick={page.loadMore}>{t('billingInvoicesMore')}</Button>;
  return <Card split noBodyPadding className="studio-plan-invoices" footer={more}>
    <header className="studio-plan-invoices__head">
      <Text.subheading contrast={6} left={<IconReceipt />}>{t('billingInvoicesEyebrow')}</Text.subheading>
      <Text.h5 contrast={9} weight={500}>{t('billingInvoicesTitle')}</Text.h5>
    </header>
    {body}
  </Card>;
}
